import React, { useState, useEffect } from 'react';
import { Bell, Mail, Smartphone, Clock, Shield, Bug, FileText, Check, RotateCcw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const deliveryOptions = [
  { value: 'in_app', label: 'In-App Only', icon: Smartphone },
  { value: 'email', label: 'Email Only', icon: Mail },
  { value: 'both', label: 'Both', icon: Bell },
  { value: 'none', label: 'Disabled', icon: null }
];

const DeliverySelector = ({ value, onChange, label }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700/50 last:border-0">
      <span className="text-sm text-slate-300">{label}</span>
      <div className="flex gap-1">
        {deliveryOptions.map(option => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
              value === option.value
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
            }`}
            title={option.label}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const NotificationPreferences = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState(null);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch preferences');
      const data = await response.json();
      setPreferences(data);
    } catch (err) {
      console.error('Error fetching preferences:', err);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (!response.ok) throw new Error('Failed to update preferences');
      const data = await response.json();
      setPreferences(data);
      toast.success('Preferences saved');
    } catch (err) {
      console.error('Error updating preferences:', err);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const resetPreferences = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to reset preferences');
      const data = await response.json();
      setPreferences(data);
      toast.success('Preferences reset to defaults');
    } catch (err) {
      console.error('Error resetting preferences:', err);
      toast.error('Failed to reset preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    updatePreferences({ [key]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-preferences">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-400" />
            Notification Preferences
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Customize how you receive alerts and notifications
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={resetPreferences}
          disabled={saving}
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset to Defaults
        </Button>
      </div>

      {/* Security Alerts */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" />
            Security Alerts
          </CardTitle>
          <CardDescription>Configure how you receive security alerts by severity</CardDescription>
        </CardHeader>
        <CardContent>
          <DeliverySelector
            label="Critical Alerts"
            value={preferences?.critical_alerts}
            onChange={(v) => handleChange('critical_alerts', v)}
          />
          <DeliverySelector
            label="High Priority Alerts"
            value={preferences?.high_alerts}
            onChange={(v) => handleChange('high_alerts', v)}
          />
          <DeliverySelector
            label="Medium Priority Alerts"
            value={preferences?.medium_alerts}
            onChange={(v) => handleChange('medium_alerts', v)}
          />
          <DeliverySelector
            label="Low Priority Alerts"
            value={preferences?.low_alerts}
            onChange={(v) => handleChange('low_alerts', v)}
          />
        </CardContent>
      </Card>

      {/* Scan Notifications */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Bug className="w-4 h-4 text-indigo-400" />
            Scan Notifications
          </CardTitle>
          <CardDescription>Configure notifications for security scans</CardDescription>
        </CardHeader>
        <CardContent>
          <DeliverySelector
            label="Scan Started"
            value={preferences?.scan_started}
            onChange={(v) => handleChange('scan_started', v)}
          />
          <DeliverySelector
            label="Scan Completed"
            value={preferences?.scan_completed}
            onChange={(v) => handleChange('scan_completed', v)}
          />
          <DeliverySelector
            label="Scan Failed"
            value={preferences?.scan_failed}
            onChange={(v) => handleChange('scan_failed', v)}
          />
        </CardContent>
      </Card>

      {/* Module Preferences */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" />
            Module Preferences
          </CardTitle>
          <CardDescription>Enable or disable notifications per module</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-slate-300">Amidala CSPM</p>
              <p className="text-xs text-slate-500">Cloud security posture notifications</p>
            </div>
            <Switch
              checked={preferences?.amidala_enabled}
              onCheckedChange={(v) => handleChange('amidala_enabled', v)}
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-slate-300">LiteSaber Scanner</p>
              <p className="text-xs text-slate-500">Vulnerability scan notifications</p>
            </div>
            <Switch
              checked={preferences?.litesaber_enabled}
              onCheckedChange={(v) => handleChange('litesaber_enabled', v)}
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-slate-300">GRC Platform</p>
              <p className="text-xs text-slate-500">Governance and compliance notifications</p>
            </div>
            <Switch
              checked={preferences?.grc_enabled}
              onCheckedChange={(v) => handleChange('grc_enabled', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-400" />
            Email & Scheduling
          </CardTitle>
          <CardDescription>Configure email digest and quiet hours</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-slate-300">Daily Email Digest</p>
              <p className="text-xs text-slate-500">Receive a summary instead of individual emails</p>
            </div>
            <Switch
              checked={preferences?.email_digest}
              onCheckedChange={(v) => handleChange('email_digest', v)}
            />
          </div>
          {preferences?.email_digest && (
            <div className="flex items-center justify-between py-2 pl-4 border-l-2 border-slate-700">
              <span className="text-sm text-slate-400">Digest Time (UTC)</span>
              <input
                type="time"
                value={preferences?.digest_time || '09:00'}
                onChange={(e) => handleChange('digest_time', e.target.value)}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-300"
              />
            </div>
          )}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm text-slate-300">Quiet Hours</p>
              <p className="text-xs text-slate-500">Pause notifications during specific hours</p>
            </div>
            <Switch
              checked={preferences?.quiet_hours_enabled}
              onCheckedChange={(v) => handleChange('quiet_hours_enabled', v)}
            />
          </div>
          {preferences?.quiet_hours_enabled && (
            <div className="flex items-center gap-4 py-2 pl-4 border-l-2 border-slate-700">
              <div>
                <span className="text-xs text-slate-500 block mb-1">Start</span>
                <input
                  type="time"
                  value={preferences?.quiet_hours_start || '22:00'}
                  onChange={(e) => handleChange('quiet_hours_start', e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-300"
                />
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">End</span>
                <input
                  type="time"
                  value={preferences?.quiet_hours_end || '07:00'}
                  onChange={(e) => handleChange('quiet_hours_end', e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-300"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {saving && (
        <div className="fixed bottom-4 right-4 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
          <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
          <span className="text-sm text-slate-300">Saving...</span>
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;
